import { languages, defaultLocale, storageKey, supportedLocale, selectLocale, setLocale, getLocale, t } from '../i18n/index.js';
import { localizedUrl, pageRoute, syncLocaleAddress } from '../i18n/routes.mjs';
import { directionOverride } from '../i18n/direction.mjs';

let initialized = false;
function readPreference() { try { return localStorage.getItem(storageKey); } catch { return null; } }
function browserLanguages() { return navigator.languages?.length ? navigator.languages : [navigator.language]; }
const normalize = value => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().trim();

function carryLanguageInLinks(locale) {
  for (const link of document.querySelectorAll('a[href]')) {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) continue;
    const destination = localizedUrl(href, locale, location.href, { languages });
    if (!destination) continue;
    if (process.env.NODE_ENV !== 'production') {
      const override = directionOverride(location.search);
      if (override) destination.searchParams.set('ui-dir', override);
    }
    link.href = destination.pathname + destination.search + destination.hash;
  }
}

export function initLanguage() {
  if (initialized) return;
  initialized = true;
  const controls = [...document.querySelectorAll('[data-language-switcher]')];
  const native = typeof HTMLElement.prototype.showPopover === 'function';
  function sync() {
    const language = languages.find(value => value.code === getLocale());
    for (const control of controls) {
      const label = control.querySelector('[data-language-label]');
      label.textContent = label.dataset.languageLabel === 'full' ? language.nativeLabel : language.shortLabel;
      control.querySelector('[data-language-trigger]').setAttribute('aria-label', t('language.current', { language: language.nativeLabel }));
      control.querySelector('[data-language-trigger]').title = t('language.choose');
      for (const option of control.querySelectorAll('[data-language-option]')) {
        const selected = option.dataset.languageOption === language.code;
        option.setAttribute('aria-checked', String(selected));
        option.tabIndex = selected ? 0 : -1;
      }
    }
  }
  window.addEventListener('an:languagechange', () => {
    syncLocaleAddress(getLocale(), { languages });
    carryLanguageInLinks(getLocale());
    sync();
    for (const control of controls) {
      if (control.querySelector('[data-language-trigger]').getAttribute('aria-expanded') === 'true') {
        control.dispatchEvent(new Event('an:reposition'));
      }
    }
  });
  function close(panel, trigger, focus = false) {
    if (native && panel.matches(':popover-open')) panel.hidePopover();
    else if (!native) { panel.hidden = true; panel.dataset.open = 'false'; trigger.setAttribute('aria-expanded', 'false'); }
    if (focus) trigger.focus();
  }
  for (const control of controls) {
    const trigger = control.querySelector('[data-language-trigger]');
    const panel = control.querySelector('[data-language-panel]');
    const search = control.querySelector('[data-language-search]');
    const options = [...control.querySelectorAll('[data-language-option]')];
    const empty = control.querySelector('[data-language-empty]');
    if (!native) { trigger.removeAttribute('popovertarget'); panel.removeAttribute('popover'); panel.hidden = true; }
    function position() {
      const anchor = trigger.getBoundingClientRect();
      const width = panel.offsetWidth, height = panel.offsetHeight, gutter = 12;
      const rtl = getComputedStyle(control).direction === 'rtl';
      let left = rtl ? anchor.left : anchor.right - width;
      left = Math.max(gutter, Math.min(left, innerWidth - width - gutter));
      const below = innerHeight - anchor.bottom - gutter;
      const above = anchor.top - gutter;
      const preferAbove = control.dataset.placement === 'above';
      const placeAbove = preferAbove ? above >= height || above > below : below < height && above > below;
      const top = placeAbove ? anchor.top - height - 8 : anchor.bottom + 8;
      panel.style.left = `${left}px`;
      panel.style.top = `${Math.max(gutter, Math.min(top, innerHeight - height - gutter))}px`;
    }
    function opened(open) {
      panel.dataset.open = String(open);
      if (!open) { trigger.setAttribute('aria-expanded', 'false'); return; }
      search.value = ''; options.forEach(option => { option.hidden = false; }); empty.hidden = true;
      position(); panel.style.visibility = ''; trigger.setAttribute('aria-expanded', 'true'); queueMicrotask(() => {
        const touch = window.matchMedia('(pointer: coarse)').matches;
        (touch ? options.find(option => option.getAttribute('aria-checked') === 'true') ?? search : search).focus();
      });
    }
    trigger.addEventListener('click', () => {
      if (native) return;
      const open = panel.hidden;
      controls.filter(other => other !== control).forEach(other => close(other.querySelector('[data-language-panel]'), other.querySelector('[data-language-trigger]')));
      panel.hidden = !open; opened(open);
    });
    if (native) {
      // Native popovers have no dimensions while closed. Reveal only after positioning.
      panel.addEventListener('beforetoggle', event => { if (event.newState === 'open') panel.style.visibility = 'hidden'; });
      panel.addEventListener('toggle', event => opened(event.newState === 'open'));
    }
    trigger.addEventListener('keydown', event => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      if (native) panel.showPopover(); else { panel.hidden = false; opened(true); }
    });
    search.addEventListener('input', () => {
      const query = normalize(search.value);
      const names = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames([getLocale()], { type: 'language' }) : null;
      options.forEach(option => {
        const language = languages.find(value => value.code === option.dataset.languageOption);
        option.hidden = !normalize([language.code, language.nativeLabel, names?.of(language.code) ?? ''].join(' ')).includes(query);
      });
      empty.hidden = options.some(option => !option.hidden); position();
    });
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(panel, trigger, true); return; }
      const visible = options.filter(option => !option.hidden);
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) && visible.length) {
        event.preventDefault(); const index = visible.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : event.key === 'ArrowDown' ? (index + 1) % visible.length : (index - 1 + visible.length) % visible.length;
        visible[next].focus();
      }
    });
    options.forEach(option => option.addEventListener('click', async () => {
      const selected = await setLocale(option.dataset.languageOption, { refresh: true });
      try { localStorage.setItem(storageKey, selected); } catch { /* The locale path preserves the choice. */ }
      close(panel, trigger, true);
    }));
    window.addEventListener('resize', () => { if (trigger.getAttribute('aria-expanded') === 'true') position(); }, { passive: true });
    control.addEventListener('an:reposition', position);
    if ('ResizeObserver' in window) new ResizeObserver(() => {
      if (trigger.getAttribute('aria-expanded') === 'true') position();
    }).observe(panel);
    window.addEventListener('scroll', () => { if (trigger.getAttribute('aria-expanded') === 'true') position(); }, { passive: true, capture: true });
    if (!native) document.addEventListener('click', event => { if (!control.contains(event.target)) close(panel, trigger); });
  }
  sync();
  const query = new URL(location.href).searchParams.get('lang');
  const rendered = supportedLocale(document.documentElement.lang);
  const routeLocale = pageRoute(location.pathname, { languages })?.locale;
  const initial = supportedLocale(query) ?? routeLocale ?? (rendered && rendered !== defaultLocale ? rendered : selectLocale({ saved: readPreference(), browser: browserLanguages() }));
  setLocale(initial).then(locale => {
    if (supportedLocale(query) || (process.env.NODE_ENV !== 'production' && directionOverride(location.search))) carryLanguageInLinks(locale);
  });
  window.addEventListener('storage', event => {
    if (event.key !== storageKey && event.key !== null) return;
    if (pageRoute(location.pathname, { languages })?.locale || supportedLocale(new URL(location.href).searchParams.get('lang'))) return;
    setLocale(selectLocale({ saved: event.newValue, browser: browserLanguages() }));
  });
}
